import React from "react";
import {
  Search,
  CalendarCheck,
  MessageSquare,
  Star,
  ShieldCheck,
  Heart,
  Users,
  Briefcase,
  FileCheck,
  Clock,
  DollarSign,
  Lock,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  MapPin,
  BadgeCheck,
  Smartphone,
  HandHeart,
  TrendingUp,
  PartyPopper,
} from "lucide-react";

const BRAND = "#87CEEB";
const BRAND_DARK = "#4FA3C7";
const INK = "#0F2742";

const Section = ({ id, bg = "white", children, className = "" }) => (
  <section
    id={id}
    data-testid={`marketing-section-${id}`}
    className={`w-full px-6 py-24 sm:px-10 lg:px-16 ${className}`}
    style={{ background: bg }}
  >
    <div className="mx-auto max-w-7xl">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <span
    className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em]"
    style={{ background: "#EAF6FC", color: BRAND_DARK }}
  >
    <Sparkles className="h-3.5 w-3.5" />
    {children}
  </span>
);

const StepCard = ({ num, title, desc, Icon, accent = BRAND }) => (
  <div className="relative flex flex-col rounded-3xl border border-slate-100 bg-white p-7 shadow-[0_8px_30px_-12px_rgba(15,39,66,0.15)]">
    <div className="mb-5 flex items-center justify-between">
      <div
        className="flex h-12 w-12 items-center justify-center rounded-2xl text-white"
        style={{ background: accent }}
      >
        <Icon className="h-6 w-6" />
      </div>
      <span
        className="text-5xl font-black leading-none tracking-tight"
        style={{ color: "#EAF6FC" }}
      >
        {num}
      </span>
    </div>
    <h3 className="text-lg font-bold" style={{ color: INK }}>
      {title}
    </h3>
    <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
  </div>
);

const BenefitCard = ({ Icon, title, desc, tint = BRAND }) => (
  <div className="flex gap-4 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
    <div
      className="flex h-12 w-12 flex-none items-center justify-center rounded-xl"
      style={{ background: `${tint}22`, color: tint }}
    >
      <Icon className="h-6 w-6" />
    </div>
    <div>
      <h4 className="text-base font-bold" style={{ color: INK }}>
        {title}
      </h4>
      <p className="mt-1 text-sm leading-relaxed text-slate-600">{desc}</p>
    </div>
  </div>
);

const PhoneMock = ({ title, lines, badge }) => (
  <div className="relative mx-auto w-[280px] rounded-[2.5rem] bg-slate-900 p-3 shadow-2xl">
    <div className="rounded-[2rem] bg-white p-5">
      <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-slate-200" />
      {badge && (
        <div
          className="mb-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold"
          style={{ background: "#EAF6FC", color: BRAND_DARK }}
        >
          <BadgeCheck className="h-3 w-3" /> {badge}
        </div>
      )}
      <h5 className="text-sm font-bold" style={{ color: INK }}>
        {title}
      </h5>
      <div className="mt-3 space-y-2">
        {lines.map((line, i) => (
          <div
            key={i}
            className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-xs text-slate-700"
          >
            <CheckCircle2
              className="h-3.5 w-3.5 flex-none"
              style={{ color: BRAND_DARK }}
            />
            <span>{line}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function MarketingPage() {
  return (
    <main className="min-h-screen bg-white font-sans antialiased" data-testid="marketing-page" style={{ color: INK }}>
      {/* ============ HERO ============ */}
      <Section id="hero" bg="linear-gradient(180deg, #EAF6FC 0%, #FFFFFF 100%)">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Australia's people-first NDIS marketplace</Eyebrow>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-sm border" style={{ borderColor: `${BRAND}66`, color: BRAND_DARK }} data-testid="ndis-registered-pill">
              <ShieldCheck className="h-3.5 w-3.5" />
              Registered NDIS Provider
            </div>
            <h1
              className="mt-6 text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
              style={{ color: INK }}
            >
              Real support.
              <br />
              <span style={{ color: BRAND_DARK }}>Real people.</span>
              <br />
              Zero fees for participants.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              BTR Health connects NDIS participants directly with verified support
              workers — no middlemen, no surprise fees. Search, book, and
              message in minutes.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <div
                className="flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white"
                style={{ background: BRAND_DARK }}
              >
                <Heart className="h-4 w-4" /> I'm looking for support
              </div>
              <div
                className="flex items-center gap-2 rounded-full border-2 px-5 py-3 text-sm font-semibold"
                style={{ borderColor: BRAND_DARK, color: BRAND_DARK }}
              >
                <Briefcase className="h-4 w-4" /> I'm providing support
              </div>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" style={{ color: BRAND_DARK }} />
                NDIS Worker Screening verified
              </div>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" style={{ color: BRAND_DARK }} />
                100% Australian-owned
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" style={{ color: BRAND_DARK }} />
                $0 platform fees
              </div>
            </div>
          </div>

          <div className="relative">
            <div
              className="absolute -inset-6 rounded-[3rem] opacity-40 blur-3xl"
              style={{ background: BRAND }}
            />
            <div className="relative grid grid-cols-2 gap-5">
              <PhoneMock
                title="Find your worker"
                badge="Participant"
                lines={[
                  "Sarah M. · 4.9 ★ · Sydney",
                  "Speaks English, Mandarin",
                  "Available this week",
                  "$65/hr · verified",
                ]}
              />
              <PhoneMock
                title="New job offer"
                badge="Support Worker"
                lines={[
                  "Personal care · 3 hrs",
                  "Wed 14 Feb, 10:00am",
                  "Bondi · 4.2km away",
                  "Tap to accept",
                ]}
              />
            </div>
          </div>
        </div>
      </Section>

      {/* ============ TWO PATHS ============ */}
      <Section id="two-paths" bg="white">
        <div className="text-center">
          <Eyebrow>Two ways in. One trusted platform.</Eyebrow>
          <h2 className="mt-5 text-3xl font-black sm:text-4xl">
            Whichever side you're on, we made it simple.
          </h2>
        </div>
        <div className="mt-14 grid gap-8 md:grid-cols-2">
          <div
            className="relative overflow-hidden rounded-3xl p-10 text-white shadow-xl"
            style={{ background: `linear-gradient(135deg, ${BRAND_DARK}, #2d7ba0)` }}
          >
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
            <Heart className="h-10 w-10" />
            <h3 className="mt-6 text-2xl font-bold">Looking for support</h3>
            <p className="mt-3 text-white/90">
              For NDIS participants and their families.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Browse hundreds of verified support workers",
                "Filter by language, skills, location, hourly rate",
                "Message workers before you book",
                "Pay only the worker's rate — no platform fees",
                "Self-managed, plan-managed & NDIA-managed welcome",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative overflow-hidden rounded-3xl border-2 p-10 shadow-xl" style={{ borderColor: BRAND }}>
            <div
              className="absolute -right-10 -top-10 h-48 w-48 rounded-full opacity-30"
              style={{ background: BRAND }}
            />
            <Briefcase className="h-10 w-10" style={{ color: BRAND_DARK }} />
            <h3 className="mt-6 text-2xl font-bold" style={{ color: INK }}>
              Providing support
            </h3>
            <p className="mt-3 text-slate-600">
              For independent workers and small support businesses.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "Choose: work independently or with an employer",
                "Upload docs once — get verified, stay verified",
                "Receive booking requests that fit your skills",
                "Set your own rates and availability",
                "Get paid faster — invoicing built in",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2
                    className="mt-0.5 h-5 w-5 flex-none"
                    style={{ color: BRAND_DARK }}
                  />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ============ HOW IT WORKS — PARTICIPANTS ============ */}
      <Section id="how-participants" bg="#F7FBFD">
        <div className="text-center">
          <Eyebrow>How it works — For people looking for support</Eyebrow>
          <h2 className="mt-5 text-3xl font-black sm:text-4xl">
            From sign-up to your first shift — in 4 easy steps.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600">
            No paperwork mountains. No call-centre runarounds. Just a friendly,
            modern way to find the right worker for you.
          </p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StepCard
            num="01"
            Icon={Users}
            title="Create your free account"
            desc="Tell us a little about your NDIS plan (self, plan or NDIA managed) and the support you need. Takes 2 minutes."
          />
          <StepCard
            num="02"
            Icon={Search}
            title="Search the directory"
            desc="Filter workers by location, language, skills, hourly rate and availability. View bios, ratings and reviews."
          />
          <StepCard
            num="03"
            Icon={MessageSquare}
            title="Message and meet"
            desc="Chat with workers before you book. Ask questions, agree on goals, build trust — all inside the app."
          />
          <StepCard
            num="04"
            Icon={CalendarCheck}
            title="Book your shift"
            desc="Pick a date and time, send the booking request, and you're set. Reminders sent automatically."
          />
        </div>
      </Section>

      {/* ============ HOW IT WORKS — WORKERS ============ */}
      <Section id="how-workers" bg="white">
        <div className="text-center">
          <Eyebrow>How it works — For support workers</Eyebrow>
          <h2 className="mt-5 text-3xl font-black sm:text-4xl">
            Get verified, get visible, get booked.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600">
            Whether you're a seasoned independent or just starting out, we make
            it easy to grow a sustainable support business.
          </p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StepCard
            num="01"
            accent={BRAND_DARK}
            Icon={Briefcase}
            title="Choose your path"
            desc="Sign up as an independent worker or under an employer organisation. We support both."
          />
          <StepCard
            num="02"
            accent={BRAND_DARK}
            Icon={FileCheck}
            title="Upload your docs"
            desc="Worker Screening Check, First Aid, Police Check, Insurance — upload once, our team reviews fast."
          />
          <StepCard
            num="03"
            accent={BRAND_DARK}
            Icon={Sparkles}
            title="Build your profile"
            desc="Add a bio, photo, skills, languages, hourly rate and weekly availability. Stand out to the right participants."
          />
          <StepCard
            num="04"
            accent={BRAND_DARK}
            Icon={HandHeart}
            title="Accept shifts you love"
            desc="Get matched with participants in your area. Accept or decline each shift — you're always in control."
          />
        </div>
      </Section>

      {/* ============ BENEFITS ============ */}
      <Section id="benefits" bg="#F7FBFD">
        <div className="grid items-start gap-12 lg:grid-cols-3">
          <div className="lg:sticky lg:top-10">
            <Eyebrow>Why BTR Health</Eyebrow>
            <h2 className="mt-5 text-3xl font-black sm:text-4xl">
              Built around what matters — people, not paperwork.
            </h2>
            <p className="mt-4 text-slate-600">
              We rebuilt the support marketplace from the ground up to put
              participants and workers first. Every feature, every rule, every
              dollar.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-2">
            <BenefitCard
              Icon={DollarSign}
              title="$0 fees for participants"
              desc="100% of your NDIS budget goes to your worker. We don't take a cut from your plan."
            />
            <BenefitCard
              Icon={ShieldCheck}
              title="Verified, screened workers"
              desc="Every worker is checked: NDIS Worker Screening, Police Check, First Aid, insurance — all reviewed by our admin team."
            />
            <BenefitCard
              Icon={Smartphone}
              title="Web + iOS + Android"
              desc="Use BTR Health anywhere. Same account, same data, in your browser or in your pocket."
              tint="#7B61FF"
            />
            <BenefitCard
              Icon={MessageSquare}
              title="Real-time messaging"
              desc="Chat with your worker or participant inside the app. No exchanging numbers, no privacy risks."
            />
            <BenefitCard
              Icon={Star}
              title="Honest reviews"
              desc="See real ratings from real bookings. We never let workers buy or fake reviews."
              tint="#F59E0B"
            />
            <BenefitCard
              Icon={Lock}
              title="Your data, protected"
              desc="Australian-hosted, encrypted in transit and at rest. We never sell your information."
              tint="#10B981"
            />
            <BenefitCard
              Icon={Clock}
              title="Booking reminders"
              desc="Automatic SMS + email reminders 24h and 1h before every shift — never miss support again."
            />
            <BenefitCard
              Icon={TrendingUp}
              title="Made to grow with you"
              desc="From your first shift to your hundredth — invoicing, timesheets and reports built in."
              tint="#7B61FF"
            />
          </div>
        </div>
      </Section>

      {/* ============ FEATURE WALK-THROUGH ============ */}
      <Section id="how-to-use" bg="white">
        <div className="text-center">
          <Eyebrow>How to use BTR Health</Eyebrow>
          <h2 className="mt-5 text-3xl font-black sm:text-4xl">
            Search. Book. Connect. Repeat.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600">
            A quick tour of the day-to-day experience for participants.
          </p>
        </div>

        <div className="mt-16 space-y-20">
          {/* Feature 1 */}
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ background: BRAND_DARK }}
              >
                1
              </span>
              <h3 className="mt-5 text-2xl font-bold sm:text-3xl">
                Search the directory
              </h3>
              <p className="mt-3 text-slate-600">
                Filter by location, language, hourly rate, skills and
                availability. See verified badges, ratings and bios at a glance.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {[
                  "Location-based search with radius",
                  "Languages spoken (over 30 supported)",
                  "Specialist skills (mental health, physical disability, autism…)",
                  "Hourly rate sliders + availability filter",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" style={{ color: BRAND_DARK }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div
                className="absolute -inset-4 rounded-3xl opacity-40 blur-2xl"
                style={{ background: BRAND }}
              />
              <div className="relative rounded-3xl bg-white p-6 shadow-xl ring-1 ring-slate-100">
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3">
                  <Search className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Sydney, NSW</span>
                  <div className="ml-auto rounded-md px-2 py-0.5 text-xs font-semibold text-white" style={{ background: BRAND_DARK }}>
                    142 workers
                  </div>
                </div>
                <div className="mt-4 flex gap-2 overflow-hidden">
                  {["English", "Mandarin", "Personal care", "$50–80/hr"].map((c) => (
                    <span key={c} className="rounded-full bg-slate-100 px-3 py-1 text-xs">
                      {c}
                    </span>
                  ))}
                </div>
                <div className="mt-5 space-y-3">
                  {[
                    { n: "Sarah M.", r: 4.9, p: "$65/hr", l: "Bondi, NSW" },
                    { n: "Daniel K.", r: 4.8, p: "$72/hr", l: "Parramatta" },
                    { n: "Priya R.", r: 5.0, p: "$58/hr", l: "Chatswood" },
                  ].map((w) => (
                    <div key={w.n} className="flex items-center gap-3 rounded-2xl border border-slate-100 p-3">
                      <div
                        className="flex h-12 w-12 flex-none items-center justify-center rounded-full text-sm font-bold text-white"
                        style={{ background: BRAND_DARK }}
                      >
                        {w.n.split(" ")[0][0]}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{w.n}</span>
                          <BadgeCheck className="h-4 w-4" style={{ color: BRAND_DARK }} />
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <MapPin className="h-3 w-3" /> {w.l}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{w.p}</div>
                        <div className="flex items-center justify-end gap-1 text-xs text-amber-500">
                          <Star className="h-3 w-3 fill-current" /> {w.r}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Feature 2 */}
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl bg-white p-6 shadow-xl ring-1 ring-slate-100">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-bold text-white"
                    style={{ background: BRAND_DARK }}
                  >
                    SM
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Book Sarah M.</div>
                    <div className="text-xs text-slate-500">Personal care · Bondi</div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">Date</div>
                    <div className="font-semibold">Wed, 14 Feb</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">Time</div>
                    <div className="font-semibold">10:00 — 13:00</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">Duration</div>
                    <div className="font-semibold">3 hours</div>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">Rate</div>
                    <div className="font-semibold">$65/hr</div>
                  </div>
                </div>
                <div
                  className="mt-5 flex items-center justify-between rounded-2xl p-4 text-white"
                  style={{ background: BRAND_DARK }}
                >
                  <span className="text-sm font-medium">Total to NDIS plan</span>
                  <span className="text-xl font-black">$195.00</span>
                </div>
                <button
                  className="mt-4 w-full rounded-2xl py-3 text-sm font-bold text-white"
                  style={{ background: INK }}
                >
                  Confirm booking →
                </button>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ background: BRAND_DARK }}
              >
                2
              </span>
              <h3 className="mt-5 text-2xl font-bold sm:text-3xl">
                Book in seconds
              </h3>
              <p className="mt-3 text-slate-600">
                Choose your date, time and duration. We auto-calculate the cost
                using your worker's rate — and your worker gets an instant
                notification.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {[
                  "Recurring bookings supported (weekly, fortnightly)",
                  "Add notes & goals for the worker",
                  "Auto-reminders 24h and 1h before",
                  "Cancel anytime up to 24h before",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" style={{ color: BRAND_DARK }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature 3 */}
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ background: BRAND_DARK }}
              >
                3
              </span>
              <h3 className="mt-5 text-2xl font-bold sm:text-3xl">
                Message in real time
              </h3>
              <p className="mt-3 text-slate-600">
                Chat with your worker before, during and after each shift. Share
                goals, give updates, build a relationship that lasts.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {[
                  "Live messaging (WebSocket powered)",
                  "Photo & document sharing",
                  "Read receipts and typing indicators",
                  "Push notifications on mobile",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" style={{ color: BRAND_DARK }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-white p-6 shadow-xl ring-1 ring-slate-100">
              <div className="mb-3 flex items-center gap-3 border-b border-slate-100 pb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: BRAND_DARK }}>
                  SM
                </div>
                <div>
                  <div className="text-sm font-semibold">Sarah M.</div>
                  <div className="text-xs text-emerald-500">● online</div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="max-w-[75%] rounded-2xl rounded-bl-md bg-slate-100 px-4 py-2.5 text-sm">
                  Hi! Looking forward to Wednesday — anything specific you'd like
                  me to bring?
                </div>
                <div
                  className="ml-auto max-w-[75%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm text-white"
                  style={{ background: BRAND_DARK }}
                >
                  Hi Sarah! Just yourself — we'll head to the shops together 😊
                </div>
                <div className="max-w-[75%] rounded-2xl rounded-bl-md bg-slate-100 px-4 py-2.5 text-sm">
                  Perfect. See you at 10am!
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-400">
                Type a message…
              </div>
            </div>
          </div>

          {/* Feature 4 */}
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1 rounded-3xl bg-white p-6 shadow-xl ring-1 ring-slate-100">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-500">
                  Shift complete
                </div>
                <PartyPopper className="h-5 w-5" style={{ color: BRAND_DARK }} />
              </div>
              <h4 className="mt-2 text-lg font-bold">How was your time with Sarah?</h4>
              <div className="mt-4 flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className="h-7 w-7 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>
              <div className="mt-4 rounded-2xl bg-slate-50 p-3 text-sm text-slate-700">
                "Sarah was wonderful — patient, kind, and really understood what
                I needed. Will definitely book again."
              </div>
              <button
                className="mt-4 w-full rounded-2xl py-3 text-sm font-bold text-white"
                style={{ background: BRAND_DARK }}
              >
                Post review
              </button>
            </div>
            <div className="order-1 lg:order-2">
              <span
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white"
                style={{ background: BRAND_DARK }}
              >
                4
              </span>
              <h3 className="mt-5 text-2xl font-bold sm:text-3xl">
                Rate, review, rebook
              </h3>
              <p className="mt-3 text-slate-600">
                After each shift, leave an honest review. Help the community,
                and re-book your favourite workers in a single tap.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {[
                  "5-star ratings with comments",
                  "One-tap favourites",
                  "Re-book past workers instantly",
                  "Reviews verified — only after a real shift",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" style={{ color: BRAND_DARK }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* ============ TRUST + CTA ============ */}
      <Section id="cta" bg="#F7FBFD">
        <div
          className="relative overflow-hidden rounded-[2.5rem] p-12 text-white shadow-2xl sm:p-16"
          style={{ background: `linear-gradient(135deg, ${BRAND_DARK}, ${INK})` }}
        >
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10" />
          <div className="absolute -bottom-32 -left-10 h-72 w-72 rounded-full bg-white/5" />
          <div className="relative grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Eyebrow>Get started today</Eyebrow>
              <h2 className="mt-5 text-3xl font-black leading-tight sm:text-5xl">
                Australia's friendliest NDIS marketplace is one click away.
              </h2>
              <p className="mt-4 max-w-md text-white/85">
                Whether you need support or you're ready to provide it, joining
                takes 2 minutes and costs nothing.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold" style={{ color: BRAND_DARK }}>
                  I'm looking for support <ArrowRight className="h-4 w-4" />
                </div>
                <div className="flex items-center gap-2 rounded-full border-2 border-white/40 px-5 py-3 text-sm font-bold text-white">
                  I'm providing support <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              {[
                { k: "$0", v: "Fees for participants" },
                { k: "100%", v: "Verified workers" },
                { k: "24/7", v: "Messaging" },
                { k: "AU", v: "Owned & hosted" },
              ].map((s) => (
                <div key={s.k} className="rounded-2xl bg-white/10 p-6 backdrop-blur">
                  <div className="text-4xl font-black">{s.k}</div>
                  <div className="mt-1 text-sm text-white/80">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-10 text-center text-sm text-slate-500">
          BTR Health · Australian-owned · NDIS-aware · btrhealth.com.au
        </div>
      </Section>
    </main>
  );
}
